import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowLeft, Coins, Gift, TrendingUp, History, Loader2, Check, Star, Zap, Crown, Gem, Rocket, CheckCircle, XCircle, ShoppingBag, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { cn, formatNumber, getInitials } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGE_ICONS = {
  starter: Coins,
  popular: Star,
  best_value: Zap,
  premium: Crown,
  ultimate: Gem
};

export default function CoinsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'cancelled' | null
  const [balance, setBalance] = useState({ coins: 0, total_earned: 0, total_spent: 0 });
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [buying, setBuying] = useState(null);
  const [activeTab, setActiveTab] = useState('buy');
  const [shopItems, setShopItems] = useState([]);
  const [shopLoading, setShopLoading] = useState(false);
  const [buyingItem, setBuyingItem] = useState(null);

  // Handle PayPal callback
  useEffect(() => {
    const handlePayPalCallback = async () => {
      const paypalToken = searchParams.get('token');
      const payerId = searchParams.get('PayerID');
      
      // Check if this is a success callback
      if (location.pathname.includes('/success') && paypalToken && payerId) {
        setCapturing(true);
        try {
          await axios.post(
            `${API}/coins/capture/${paypalToken}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPaymentStatus('success');
          toast.success('Payment successful! Coins added to your wallet.');
          // Refresh balance
          const balanceRes = await axios.get(`${API}/coins/balance`, { 
            headers: { Authorization: `Bearer ${token}` } 
          });
          setBalance(balanceRes.data);
          // Fetch updated transactions
          const transactionsRes = await axios.get(`${API}/coins/transactions`, { 
            headers: { Authorization: `Bearer ${token}` } 
          });
          setTransactions(transactionsRes.data);
        } catch (error) {
          console.error('Failed to capture payment:', error);
          toast.error('Failed to process payment. Please contact support.');
          setPaymentStatus('error');
        } finally {
          setCapturing(false);
        }
      } else if (location.pathname.includes('/cancel')) {
        setPaymentStatus('cancelled');
        toast.info('Payment cancelled.');
      }
    };

    if (token) {
      handlePayPalCallback();
    }
  }, [location.pathname, searchParams, token]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [balanceRes, packagesRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/coins/balance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/coins/packages`),
        axios.get(`${API}/coins/transactions`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBalance(balanceRes.data);
      setPackages(packagesRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Failed to fetch coin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const buyCoins = async (packageId) => {
    setBuying(packageId);
    try {
      const response = await axios.post(`${API}/coins/buy`, 
        { 
          package_id: packageId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.approval_url) {
        // Redirect to PayPal
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setBuying(null);
    }
  };

  // === Coin Shop ===
  const fetchShopItems = async () => {
    setShopLoading(true);
    try {
      const res = await axios.get(`${API}/coin-shop/items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShopItems(res.data);
    } catch (error) {
      toast.error('Failed to load shop items');
    } finally {
      setShopLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'shop' && shopItems.length === 0) {
      fetchShopItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Gift recipient picker state
  const [giftItem, setGiftItem] = useState(null);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipientResults, setRecipientResults] = useState([]);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [sendingGift, setSendingGift] = useState(false);
  const [giftMessage, setGiftMessage] = useState('');

  // Search users (debounced)
  useEffect(() => {
    if (!giftItem || !recipientQuery || recipientQuery.length < 2) {
      setRecipientResults([]);
      return;
    }
    setRecipientLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/users/search?q=${encodeURIComponent(recipientQuery)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRecipientResults(res.data.filter((u) => u.id !== user?.id).slice(0, 8));
      } catch (e) {
        setRecipientResults([]);
      } finally {
        setRecipientLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [recipientQuery, giftItem, token, user]);

  const handleOpenGiftPicker = (item) => {
    if (balance.coins < item.price) {
      toast.error(`Not enough coins. You need ${formatNumber(item.price)} coins (≈ $${item.price_usd.toFixed(2)}).`);
      return;
    }
    setGiftItem(item);
    setRecipientQuery('');
    setRecipientResults([]);
    setGiftMessage('');
  };

  const handleSendGift = async (recipient) => {
    if (!giftItem) return;
    setSendingGift(true);
    try {
      await axios.post(
        `${API}/coin-shop/send-gift`,
        {
          item_id: giftItem.id,
          recipient_id: recipient.id,
          message: giftMessage,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${giftItem.name} sent to @${recipient.username}!`);
      // Refresh balance
      const bal = await axios.get(`${API}/coins/balance`, { headers: { Authorization: `Bearer ${token}` } });
      setBalance(bal.data);
      setGiftItem(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send gift');
    } finally {
      setSendingGift(false);
    }
  };

  if (loading || capturing) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        {capturing && <p className="text-muted-foreground">Processing your payment...</p>}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="coins-page">
      {/* Payment Status Banner */}
      {paymentStatus === 'success' && (
        <div className="bg-green-500/20 border-b border-green-500/30 p-4">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <div>
              <p className="font-semibold text-green-600">Payment Successful!</p>
              <p className="text-sm text-green-600/80">Your coins have been added to your wallet.</p>
            </div>
          </div>
        </div>
      )}
      {paymentStatus === 'cancelled' && (
        <div className="bg-yellow-500/20 border-b border-yellow-500/30 p-4">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <XCircle className="w-6 h-6 text-yellow-600" />
            <div>
              <p className="font-semibold text-yellow-600">Payment Cancelled</p>
              <p className="text-sm text-yellow-600/80">No charges were made. Try again anytime!</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/feed')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-bold text-lg">Coins</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                <Coins className="w-10 h-10 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">Your Balance</p>
              <p className="text-4xl font-bold text-primary mt-1">{formatNumber(balance.coins)}</p>
              <p className="text-sm text-muted-foreground mt-1">coins</p>
            </div>
            
            <div className="flex justify-around mt-6 pt-6 border-t border-border/50">
              <div className="text-center">
                <TrendingUp className="w-5 h-5 text-green-500 mx-auto mb-1" />
                <p className="text-lg font-semibold">{formatNumber(balance.total_earned)}</p>
                <p className="text-xs text-muted-foreground">Earned</p>
              </div>
              <div className="text-center">
                <Gift className="w-5 h-5 text-pink-500 mx-auto mb-1" />
                <p className="text-lg font-semibold">{formatNumber(balance.total_spent)}</p>
                <p className="text-xs text-muted-foreground">Spent</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'buy' ? 'default' : 'outline'}
            className="flex-1 rounded-full"
            onClick={() => setActiveTab('buy')}
            data-testid="tab-buy"
          >
            <Coins className="w-4 h-4 mr-2" />
            Buy Coins
          </Button>
          <Button
            variant={activeTab === 'shop' ? 'default' : 'outline'}
            className="flex-1 rounded-full"
            onClick={() => setActiveTab('shop')}
            data-testid="tab-shop"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Shop
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'outline'}
            className="flex-1 rounded-full"
            onClick={() => setActiveTab('history')}
            data-testid="tab-history"
          >
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
        </div>

        {/* Buy Coins Tab */}
        {activeTab === 'buy' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Choose a Package</h2>
            <div className="grid gap-4">
              {packages.map((pkg) => {
                const IconComponent = PACKAGE_ICONS[pkg.id] || Coins;
                const isPopular = pkg.id === 'best_value';
                
                return (
                  <Card 
                    key={pkg.id} 
                    className={cn(
                      "relative overflow-hidden transition-all hover:scale-[1.02]",
                      isPopular && "border-primary shadow-lg shadow-primary/20"
                    )}
                    data-testid={`coin-package-${pkg.id}`}
                  >
                    {isPopular && (
                      <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-bl-lg font-medium">
                        BEST VALUE
                      </div>
                    )}
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "w-12 h-12 rounded-xl flex items-center justify-center",
                          isPopular ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
                        )}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="font-semibold">{pkg.name}</p>
                          <p className="text-2xl font-bold text-primary">
                            {formatNumber(pkg.coins)}
                            {pkg.bonus > 0 && (
                              <span className="text-sm font-normal text-green-500 ml-2">
                                +{formatNumber(pkg.bonus)} bonus
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => buyCoins(pkg.id)}
                        disabled={buying === pkg.id}
                        className="rounded-full min-w-[80px]"
                        variant={isPopular ? 'default' : 'outline'}
                      >
                        {buying === pkg.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          `$${pkg.price}`
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              Secure payment via PayPal. Coins never expire.
            </p>
          </div>
        )}

        {/* Shop Tab — Premium Haitian-themed Gifts */}
        {activeTab === 'shop' && (
          <div className="space-y-4" data-testid="shop-tab-content">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Premium Gifts</h2>
              <p className="text-xs text-muted-foreground">Send Haitian-themed gifts to friends</p>
            </div>
            {shopLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {shopItems.map((item) => (
                  <Card
                    key={item.id}
                    className="overflow-hidden hover:shadow-lg transition-shadow"
                    data-testid={`shop-item-${item.id}`}
                  >
                    <div className="relative aspect-[16/9] bg-muted">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full capitalize">
                        {item.category}
                      </div>
                    </div>
                    <CardContent className="p-4 space-y-2">
                      <h3 className="font-semibold leading-tight">{item.name}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">
                        {item.description}
                      </p>
                      <div className="flex items-center justify-between pt-1">
                        <div className="flex flex-col leading-tight">
                          <div className="flex items-center gap-1 font-bold text-primary">
                            <Coins className="w-4 h-4" />
                            {formatNumber(item.price)}
                          </div>
                          <span className="text-[11px] text-muted-foreground">
                            ≈ ${item.price_usd.toFixed(2)} USD
                          </span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleOpenGiftPicker(item)}
                          disabled={balance.coins < item.price}
                          data-testid={`send-gift-${item.id}`}
                        >
                          <Gift className="w-3 h-3 mr-1" />
                          {balance.coins < item.price ? 'Need more coins' : 'Send as Gift'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Gift Recipient Picker Modal */}
        {giftItem && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => !sendingGift && setGiftItem(null)}
            data-testid="gift-picker-modal"
          >
            <Card
              className="w-full max-w-md max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <CardContent className="p-4 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-lg">Send as Gift</h3>
                    <p className="text-sm text-muted-foreground">
                      {giftItem.name} • {formatNumber(giftItem.price)} coins (≈ ${giftItem.price_usd.toFixed(2)})
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setGiftItem(null)} disabled={sendingGift}>
                    <XCircle className="w-5 h-5" />
                  </Button>
                </div>

                <img
                  src={giftItem.image_url}
                  alt={giftItem.name}
                  className="w-full aspect-video object-cover rounded-lg"
                />

                <div className="space-y-2">
                  <label className="text-sm font-medium">Add a message (optional)</label>
                  <textarea
                    rows={2}
                    maxLength={200}
                    value={giftMessage}
                    onChange={(e) => setGiftMessage(e.target.value)}
                    placeholder="Pou ou frè mwen!"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="gift-message-input"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Send to</label>
                  <input
                    type="text"
                    value={recipientQuery}
                    onChange={(e) => setRecipientQuery(e.target.value)}
                    placeholder="Search username or name..."
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    autoFocus
                    data-testid="recipient-search"
                  />
                  <div className="border rounded-md max-h-56 overflow-y-auto">
                    {recipientLoading && (
                      <div className="p-3 flex justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    )}
                    {!recipientLoading && recipientResults.length === 0 && recipientQuery.length >= 2 && (
                      <p className="p-3 text-sm text-muted-foreground">No users found</p>
                    )}
                    {recipientResults.map((u) => (
                      <button
                        key={u.id}
                        className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors text-left disabled:opacity-50"
                        disabled={sendingGift}
                        onClick={() => handleSendGift(u)}
                        data-testid={`gift-recipient-${u.username}`}
                      >
                        <Avatar className="w-9 h-9">
                          <AvatarImage src={u.avatar} />
                          <AvatarFallback>{getInitials(u.display_name || u.username)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{u.display_name || u.username}</p>
                          <p className="text-xs text-muted-foreground">@{u.username}</p>
                        </div>
                        {sendingGift && <Loader2 className="w-4 h-4 animate-spin" />}
                      </button>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground text-center">
                  Your balance after gift: {formatNumber(balance.coins - giftItem.price)} coins
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Transaction History</h2>
            {transactions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No transactions yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Buy coins to get started!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {transactions.map((tx) => (
                  <Card key={tx.id}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center",
                          tx.type === 'purchase' ? "bg-green-500/20 text-green-500" :
                          tx.sender_id === user?.id ? "bg-red-500/20 text-red-500" :
                          "bg-primary/20 text-primary"
                        )}>
                          {tx.type === 'purchase' ? <TrendingUp className="w-5 h-5" /> :
                           tx.sender_id === user?.id ? <Gift className="w-5 h-5" /> :
                           <Coins className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="font-medium">
                            {tx.type === 'purchase' ? 'Purchased Coins' :
                             tx.sender_id === user?.id ? `Sent to @${tx.receiver_username}` :
                             `Received from @${tx.sender_username}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(tx.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <p className={cn(
                        "font-bold",
                        tx.type === 'purchase' || tx.receiver_id === user?.id ? "text-green-500" : "text-red-500"
                      )}>
                        {tx.type === 'purchase' || tx.receiver_id === user?.id ? '+' : '-'}
                        {formatNumber(tx.amount || tx.coins)}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
